import { useEffect, useMemo, useState } from 'react';
import { Download, RotateCcw, Timer, X } from 'lucide-react';
import { SortableTh, sortRows, BulkActionsBar } from '@synotis/ui';
import { apiReports } from '../api/client.js';
import { hoursToDays } from '../lib/units.js';
import { AFFAIRE_TIME_SORT, useSortStore } from '../hooks/useSortStore.js';
import DatePicker from './DatePicker.jsx';
import AffaireFilterableSelect from './AffaireFilterableSelect.jsx';
import { exportRowsToCsv } from './reports/exportCsv.js';

/**
 * Ticket #56 — Suivi du temps par affaire (section de l'Accueil, tous rôles).
 *
 * Compare, par affaire, le temps saisi sur une période choisie au temps total
 * historique (depuis la première saisie, indépendant du filtre). Heures et/ou
 * jours (1 j = 8 h). Affaires facturables uniquement (les affaires internes —
 * formation, RH… — exposeraient le temps nominatif d'un collègue). Périmètre
 * d'affaires borné côté serveur par le rôle.
 *
 * Ticket #113 — filtre par personne (raccourcis « Moi » / « Tout le monde » +
 * liste des personnes ayant saisi sur les affaires ciblées, parties comprises),
 * cumulable avec la période et les affaires ; bouton « Réinitialiser ».
 * Visibilité (serveur, `listVisiblePersonIds`) : un collaborateur ne peut
 * filtrer que sur lui-même (pas de liste déroulante : « Moi / Tout le monde »),
 * un manager sur lui-même et son équipe directe, un administrateur sur tous.
 * Tableau : tri par colonne mémorisé, multisélection et export CSV.
 *
 * Les réglages (période, affaires, unité, personne) sont une préférence du
 * poste : mémorisés en localStorage par utilisateur, jamais indispensables au
 * rendu.
 */

const PRESETS = [
  { key: 'thisMonth', label: 'Ce mois' },
  { key: 'lastMonth', label: 'Mois dernier' },
  { key: 'thisQuarter', label: 'Ce trimestre' },
  { key: 'thisYear', label: 'Cette année' },
  { key: 'custom', label: 'Personnalisée' },
];

const UNITS = [
  { key: 'both', label: 'h + j' },
  { key: 'h', label: 'Heures' },
  { key: 'd', label: 'Jours' },
];

const DEFAULT_PRESET = 'thisMonth';
const DEFAULT_UNIT = 'both';

const iso = (d) => d.toISOString().slice(0, 10);
const utc = (y, m, d) => new Date(Date.UTC(y, m, d));

function presetWindow(key, now = new Date()) {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  switch (key) {
    case 'lastMonth':
      return { from: iso(utc(y, m - 1, 1)), to: iso(utc(y, m, 0)) };
    case 'thisQuarter': {
      const q = Math.floor(m / 3) * 3;
      return { from: iso(utc(y, q, 1)), to: iso(utc(y, q + 3, 0)) };
    }
    case 'thisYear':
      return { from: iso(utc(y, 0, 1)), to: iso(utc(y, 11, 31)) };
    case 'thisMonth':
    default:
      return { from: iso(utc(y, m, 1)), to: iso(utc(y, m + 1, 0)) };
  }
}

const storageKey = (userId) => `synolog.home.affaireTime.${userId ?? 'anon'}`;

function loadPrefs(userId) {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && typeof p === 'object' ? p : null;
  } catch {
    return null;
  }
}

function savePrefs(userId, prefs) {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
  } catch {
    /* stockage indisponible : préférence non mémorisée, sans impact */
  }
}

/** Couleur d'affaire stockée avec ou sans `#` (import Float : sans). */
function cssColor(raw) {
  if (!raw) return 'var(--glass-border)';
  return raw.startsWith('#') ? raw : `#${raw}`;
}

const nf = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

function TimeValue({ hours, unit, strong = false }) {
  const h = `${nf.format(hours)} h`;
  const d = `${nf.format(hoursToDays(hours))} j`;
  const main = { color: 'var(--text)', fontWeight: strong ? 600 : 500 };
  if (unit === 'h') return <span style={main}>{h}</span>;
  if (unit === 'd') return <span style={main}>{d}</span>;
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
      <span style={main}>{h}</span>
      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{d}</span>
    </span>
  );
}

function Pill({ active, onClick, children, disabled = false, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      disabled={disabled}
      title={title}
      style={{
        background: active ? 'var(--accent)' : 'transparent',
        color: active ? '#fff' : 'var(--text)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-pill)',
        padding: '5px 12px',
        fontSize: 12,
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

const th = {
  textAlign: 'right',
  padding: '8px 10px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-muted)',
  borderBottom: '1px solid var(--glass-border)',
  whiteSpace: 'nowrap',
};
const td = {
  textAlign: 'right',
  padding: '8px 10px',
  fontSize: 13,
  borderBottom: '1px solid var(--glass-border)',
  verticalAlign: 'top',
};
const checkTd = { ...td, textAlign: 'center', width: 32, padding: '8px 6px' };
const selectStyle = {
  background: 'var(--card-bg)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '5px 8px',
  fontSize: 12,
  maxWidth: '100%',
};

/** Valeur brute pour l'export : nombre à point décimal, 2 décimales max. */
const raw2 = (n) => (n == null || !Number.isFinite(Number(n)) ? '' : Math.round(Number(n) * 100) / 100);

const SORT_GETTERS = {
  name: (r) => r.name,
  client: (r) => r.clientName,
  periodHours: (r) => r.periodHours,
  periodBillableHours: (r) => r.periodBillableHours,
  periodNonBillableHours: (r) => r.periodNonBillableHours,
  totalHours: (r) => r.totalHours,
};

export default function AffaireTimeSection({ user, sectionTitle }) {
  const userId = user?.id;
  const initial = useMemo(() => loadPrefs(userId), [userId]);
  const [preset, setPreset] = useState(initial?.preset ?? 'thisMonth');
  const [custom, setCustom] = useState(
    initial?.custom?.from && initial?.custom?.to ? initial.custom : presetWindow('thisMonth'),
  );
  const [affaireIds, setAffaireIds] = useState(
    Array.isArray(initial?.affaireIds) ? initial.affaireIds : [],
  );
  const [unit, setUnit] = useState(initial?.unit ?? DEFAULT_UNIT);
  // #113 — personne filtrée (`null` = tout le monde ; « Moi » = myPersonId).
  const [personId, setPersonId] = useState(
    typeof initial?.personId === 'string' && initial.personId ? initial.personId : null,
  );
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const { sort, setSort } = useSortStore(AFFAIRE_TIME_SORT);

  const win = preset === 'custom' ? custom : presetWindow(preset);
  const invalidRange = !win.from || !win.to || win.from > win.to;

  useEffect(() => {
    savePrefs(userId, { preset, custom, affaireIds, unit, personId });
  }, [userId, preset, custom, affaireIds, unit, personId]);

  const idsKey = affaireIds.join(',');
  useEffect(() => {
    if (invalidRange) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiReports
      .affaireTime({ from: win.from, to: win.to, affaireIds, personId })
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((e) => {
        if (cancelled) return;
        // Personne mémorisée sortie du périmètre (403) : retour à « Tout le monde ».
        if (e.status === 403 && personId) {
          setPersonId(null);
          return;
        }
        setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [win.from, win.to, idsKey, personId, invalidRange]);

  const myPersonId = report?.myPersonId ?? null;
  const persons = report?.persons ?? [];
  // Liste déroulante réservée aux managers et administrateurs : un
  // collaborateur ne peut filtrer que sur lui-même (« Moi »).
  const canPickPerson = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  // Personne mémorisée qui n'a plus de saisie sur les affaires ciblées (ou sortie
  // du périmètre) : retour à « Tout le monde ». « Moi » est conservé même sans
  // saisie (tableau vide, mais le filtre reste celui choisi).
  useEffect(() => {
    if (!report || !personId || personId === report.myPersonId) return;
    if (!(report.persons ?? []).some((p) => p.id === personId)) setPersonId(null);
  }, [report, personId]);

  const resetAll = () => {
    setPreset(DEFAULT_PRESET);
    setCustom(presetWindow(DEFAULT_PRESET));
    setAffaireIds([]);
    setUnit(DEFAULT_UNIT);
    setPersonId(null);
  };
  const isDefault =
    preset === DEFAULT_PRESET && affaireIds.length === 0 && unit === DEFAULT_UNIT && personId === null;

  const options = report?.affaires ?? [];
  const byId = useMemo(() => new Map(options.map((a) => [a.id, a])), [options]);
  // Une affaire mémorisée sortie du périmètre (ou archivée) n'est plus proposée.
  const visibleSelected = report ? affaireIds.filter((id) => byId.has(id)) : affaireIds;
  const pickable = options.filter((a) => !affaireIds.includes(a.id));

  const rows = useMemo(() => report?.rows ?? [], [report]);
  const totals = report?.totals;
  const sorted = useMemo(() => sortRows(rows, sort, SORT_GETTERS), [rows, sort]);

  // Le jeu de lignes change (période, affaires, personne, rechargement) : la
  // sélection ne garde que les affaires encore affichées.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(rows.map((r) => r.affaireId));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const allSelected = sorted.length > 0 && sorted.every((r) => selectedIds.has(r.affaireId));
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(sorted.map((r) => r.affaireId)));
  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const selectedRows = sorted.filter((r) => selectedIds.has(r.affaireId));

  const personOption = persons.find((p) => p.id === personId);
  const personLabel = personId
    ? personId === myPersonId
      ? personOption?.name ?? 'Moi'
      : personOption?.name ?? ''
    : 'Tout le monde';

  // Export CSV des lignes sélectionnées : valeurs brutes (point décimal, pas de
  // séparateur de milliers), dans l'unité affichée (h + j ⇒ les deux).
  const exportSelected = () => {
    const units = unit === 'both' ? ['h', 'j'] : [unit === 'd' ? 'j' : 'h'];
    const metric = (key, label) =>
      units.map((u) => ({
        key,
        label: `${label} (${u})`,
        format: (v) => raw2(u === 'j' ? hoursToDays(Number(v) || 0) : v),
      }));
    const columns = [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Affaire' },
      { key: 'clientName', label: 'Client' },
      { key: 'affaireId', label: 'Du', format: () => win.from },
      { key: 'affaireId', label: 'Au', format: () => win.to },
      { key: 'affaireId', label: 'Personne', format: () => personLabel },
      ...metric('periodHours', 'Sur la période'),
      ...metric('periodBillableHours', 'Dont facturable'),
      ...metric('periodNonBillableHours', 'Dont non facturable'),
      ...metric('totalHours', 'Total historique'),
      { key: 'firstLoggedDate', label: 'Première saisie' },
    ];
    exportRowsToCsv(selectedRows, columns, `suivi_temps_affaires_${win.from}_${win.to}`);
  };

  // La personne choisie doit rester visible dans la liste même sans saisie (« Moi »).
  const personChoices =
    personId && !personOption ? [{ id: personId, name: 'Moi', archived: false }, ...persons] : persons;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h2 style={sectionTitle}>
        <Timer size={16} /> Suivi du temps par affaire
      </h2>

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-card)',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          minWidth: 0,
        }}
      >
        {/* ---- Période ---- */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 60 }}>Période</span>
          {PRESETS.map((p) => (
            <Pill key={p.key} active={preset === p.key} onClick={() => setPreset(p.key)}>
              {p.label}
            </Pill>
          ))}
          {preset === 'custom' ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <DatePicker
                value={custom.from}
                required
                max={custom.to || undefined}
                ariaLabel="Date de début"
                onChange={(v) => setCustom((c) => ({ ...c, from: v }))}
              />
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>→</span>
              <DatePicker
                value={custom.to}
                required
                min={custom.from || undefined}
                ariaLabel="Date de fin"
                onChange={(v) => setCustom((c) => ({ ...c, to: v }))}
              />
            </span>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {win.from.split('-').reverse().join('/')} → {win.to.split('-').reverse().join('/')}
            </span>
          )}
        </div>

        {/* ---- Affaires + unité ---- */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 60 }}>Affaires</span>
          <div style={{ width: 280, maxWidth: '100%' }}>
            <AffaireFilterableSelect
              affaires={pickable}
              value=""
              onChange={(id) => id && setAffaireIds((ids) => (ids.includes(id) ? ids : [...ids, id]))}
              placeholder={
                visibleSelected.length === 0 ? 'Toutes (actives sur la période)' : '+ Ajouter une affaire'
              }
              disabled={!report}
            />
          </div>
          {visibleSelected.map((id) => {
            const a = byId.get(id);
            return (
              <span
                key={id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 6px 3px 10px',
                  borderRadius: 'var(--radius-pill)',
                  border: '1px solid var(--glass-border)',
                  background: 'var(--glass)',
                  fontSize: 12,
                  color: 'var(--text)',
                }}
              >
                {a?.code ? `${a.code} · ` : ''}
                {a?.name ?? id}
                <button
                  type="button"
                  title="Retirer du filtre"
                  onClick={() => setAffaireIds((ids) => ids.filter((x) => x !== id))}
                  style={{ display: 'inline-flex', background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--text-muted)' }}
                >
                  <X size={12} />
                </button>
              </span>
            );
          })}
          {visibleSelected.length > 0 ? (
            <button
              type="button"
              onClick={() => setAffaireIds([])}
              style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 12, cursor: 'pointer' }}
            >
              Tout effacer
            </button>
          ) : null}
          <span style={{ display: 'inline-flex', gap: 6, marginLeft: 'auto' }}>
            {UNITS.map((u) => (
              <Pill key={u.key} active={unit === u.key} onClick={() => setUnit(u.key)}>
                {u.label}
              </Pill>
            ))}
          </span>
        </div>

        {/* ---- Personne (#113) ---- */}
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', minWidth: 60 }}>Personne</span>
          <Pill active={personId === null} onClick={() => setPersonId(null)}>
            Tout le monde
          </Pill>
          <Pill
            active={personId !== null && personId === myPersonId}
            disabled={!myPersonId}
            title={
              myPersonId
                ? 'Mon temps uniquement'
                : report
                  ? 'Aucune fiche collaborateur n’est liée à votre compte'
                  : 'Chargement…'
            }
            onClick={() => myPersonId && setPersonId(myPersonId)}
          >
            Moi
          </Pill>
          {canPickPerson ? (
            <select
              aria-label="Filtrer sur une personne"
              value={personId ?? ''}
              onChange={(e) => setPersonId(e.target.value || null)}
              disabled={!report}
              style={selectStyle}
            >
              <option value="">{persons.length ? 'Choisir une personne…' : 'Aucune personne'}</option>
              {personChoices.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.archived ? ' (parti·e)' : ''}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            onClick={resetAll}
            disabled={isDefault}
            title="Revenir aux réglages par défaut (ce mois, toutes les affaires, h + j, tout le monde)"
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-pill)',
              padding: '5px 12px',
              fontSize: 12,
              cursor: isDefault ? 'not-allowed' : 'pointer',
              opacity: isDefault ? 0.45 : 1,
            }}
          >
            <RotateCcw size={13} /> Réinitialiser
          </button>
        </div>

        {/* ---- Tableau ---- */}
        {invalidRange ? (
          <div style={{ color: 'var(--warning)', fontSize: 13 }}>Choisissez une date de début et une date de fin valides.</div>
        ) : error ? (
          <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>
        ) : loading && !report ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Chargement…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {visibleSelected.length > 0
              ? 'Aucune des affaires sélectionnées n’est disponible.'
              : personId
                ? 'Aucun temps saisi par cette personne sur vos affaires facturables pendant cette période.'
                : 'Aucun temps saisi sur vos affaires facturables pendant cette période.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <BulkActionsBar
              count={selectedIds.size}
              actions={[
                {
                  key: 'csv',
                  label: 'Exporter (CSV)',
                  icon: Download,
                  count: selectedRows.length,
                  onClick: exportSelected,
                },
              ]}
              onClear={() => setSelectedIds(new Set())}
            />
            <div style={{ overflowX: 'auto', opacity: loading ? 0.6 : 1, transition: 'opacity 150ms' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 680 }}>
                <thead>
                  <tr>
                    <th style={{ ...th, textAlign: 'center', width: 32, padding: '8px 6px' }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        disabled={sorted.length === 0}
                        onChange={toggleAll}
                        title="Tout sélectionner"
                        aria-label="Tout sélectionner"
                      />
                    </th>
                    <SortableTh field="name" sort={sort} onSort={setSort} style={{ ...th, textAlign: 'left' }}>
                      Affaire
                    </SortableTh>
                    <SortableTh field="client" sort={sort} onSort={setSort} style={{ ...th, textAlign: 'left' }}>
                      Client
                    </SortableTh>
                    <SortableTh field="periodHours" sort={sort} onSort={setSort} style={th}>
                      <span title="Temps saisi entre la date de début et la date de fin">Sur la période</span>
                    </SortableTh>
                    <SortableTh field="periodBillableHours" sort={sort} onSort={setSort} style={th}>
                      dont facturable
                    </SortableTh>
                    <SortableTh field="periodNonBillableHours" sort={sort} onSort={setSort} style={th}>
                      dont non fact.
                    </SortableTh>
                    <SortableTh field="totalHours" sort={sort} onSort={setSort} style={th}>
                      <span title="Tout le temps saisi depuis le début de l’affaire, indépendamment de la période">
                        Total historique
                      </span>
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((r) => (
                    <tr
                      key={r.affaireId}
                      style={{ background: selectedIds.has(r.affaireId) ? 'var(--glass)' : undefined }}
                    >
                      <td style={checkTd}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.affaireId)}
                          onChange={() => toggleOne(r.affaireId)}
                          aria-label={`Sélectionner ${r.name}`}
                        />
                      </td>
                      <td style={{ ...td, textAlign: 'left', maxWidth: 280 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <span
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: 999,
                              flexShrink: 0,
                              background: cssColor(r.color),
                            }}
                          />
                          <span style={{ minWidth: 0 }}>
                            <span style={{ color: 'var(--text)', fontWeight: 500 }}>{r.name}</span>
                            {r.code ? (
                              <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: 12 }}>{r.code}</span>
                            ) : null}
                          </span>
                        </span>
                      </td>
                      <td style={{ ...td, textAlign: 'left', color: 'var(--text-muted)' }}>{r.clientName ?? '—'}</td>
                      <td style={td}><TimeValue hours={r.periodHours} unit={unit} strong /></td>
                      <td style={td}><TimeValue hours={r.periodBillableHours} unit={unit} /></td>
                      <td style={td}><TimeValue hours={r.periodNonBillableHours} unit={unit} /></td>
                      <td
                        style={td}
                        title={r.firstLoggedDate ? `Depuis le ${r.firstLoggedDate.split('-').reverse().join('/')}` : undefined}
                      >
                        <TimeValue hours={r.totalHours} unit={unit} strong />
                      </td>
                    </tr>
                  ))}
                </tbody>
                {totals && rows.length > 1 ? (
                  <tfoot>
                    <tr>
                      <td style={{ ...checkTd, borderBottom: 'none' }} />
                      <td style={{ ...td, textAlign: 'left', fontWeight: 600, color: 'var(--text)', borderBottom: 'none' }} colSpan={2}>
                        Total ({rows.length} affaires)
                      </td>
                      <td style={{ ...td, borderBottom: 'none' }}><TimeValue hours={totals.periodHours} unit={unit} strong /></td>
                      <td style={{ ...td, borderBottom: 'none' }}><TimeValue hours={totals.periodBillableHours} unit={unit} /></td>
                      <td style={{ ...td, borderBottom: 'none' }}><TimeValue hours={totals.periodNonBillableHours} unit={unit} /></td>
                      <td style={{ ...td, borderBottom: 'none' }}><TimeValue hours={totals.totalHours} unit={unit} strong /></td>
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          Affaires facturables uniquement (hors affaires internes) ·{' '}
          {personId
            ? `temps saisi par ${personLabel || 'la personne choisie'} uniquement`
            : 'temps saisi par toute l’équipe de l’affaire'}{' '}
          · 1 jour = 8 h · le total historique ne dépend pas de la période.
        </div>
      </div>
    </section>
  );
}
